export interface LessonStep {
    title: string;
    description: string;
    imageUrl: string;
}

export interface LessonContent {
    id: string;
    title: string;
    markdown: string;
    steps?: LessonStep[];
}

export const LEARN_CONTENTS: Record<string, LessonContent> = {
    'getting-started': {
        id: 'getting-started',
        title: 'Getting Started with Magnus',
        markdown: `
# Welcome to Living Reliability
Magnus is more than just an FMEA tool. it is a **Living System** where data flows seamlessly between design, process, and field performance.

### Key Concepts:
*   **Single Source of Truth**: Data entered in Core reflects in PFMEA.
*   **AI-Native**: Use our agents to draft your first analysis in seconds.
*   **Always-in-Sync**: Cross-app data binding ensures compliance.
        `,
        steps: [
            {
                title: 'Secure Sign-In',
                description: 'Use your enterprise credentials to access the Magnus ecosystem via AutoGain Auth.',
                imageUrl: '/learn/magnus_core_login_1773450460106.png'
            },
            {
                title: 'The Central Dashboard',
                description: 'View your data quality scores, active projects, and AI-assisted workflows at a glance.',
                imageUrl: '/learn/magnus_pfmea_dashboard_1773450513054.png'
            },
            {
                title: 'Launching a Wizard',
                description: 'Start a new analysis using the Ideation Wizard to overcome "blank page syndrome".',
                imageUrl: '/learn/magnus_pfmea_ideation_wizard_1773450535322.png'
            }
        ]
    },
    'fmea-fundamentals': {
        id: 'fmea-fundamentals',
        title: 'FMEA Fundamentals',
        markdown: `
# FMEA: The Basics
Failure Mode and Effects Analysis (FMEA) is a structured approach to discovering potential failures that may exist within the design of a product or process.

### The AIAG-VDA 7-Step Process:
1. **Planning & Preparation**
2. **Structure Analysis**
3. **Function Analysis**
4. **Failure Analysis**
5. **Risk Analysis**
6. **Optimization**
7. **Results Documentation**
        `,
        steps: [
            {
                title: 'Risk Identification',
                description: 'Identify what can go wrong and what the impact is on the end-user.',
                imageUrl: '/learn/magnus_pfmea_ideation_wizard_1773450535322.png'
            }
        ]
    },
    'structure-analysis': {
        id: 'structure-analysis',
        title: 'Step 2: Structure Analysis',
        markdown: `
# Structure Analysis
The goal of Structure Analysis is to identify the system components and their hierarchical relationships.

### The 3-Tier Rule:
*   **Next Higher Level**: Where the function is consumed.
*   **Focus Element**: The item we are currently analyzing.
*   **Next Lower Level**: Components/parts that make up the Focus Element.

In Magnus, the **Analysis Tree** on the left allows you to visualize and manage this hierarchy in real-time.
        `,
        steps: [
            {
                title: 'Building the Tree',
                description: 'Drag and drop components to build your system hierarchy.',
                imageUrl: '/learn/magnus_pfmea_dashboard_1773450513054.png'
            }
        ]
    },
    'resolution-sync': {
        id: 'resolution-sync',
        title: 'Closing the Loop: Resolution Sync',
        markdown: `
# Resolution -> FMEA Sync
In the "Living Reliability" system, field failures found in **Magnus Resolve** are automatically suggested as new failure modes or causes in your **PFMEA**.

### How it works:
1. Log an incident in Resolve.
2. Flag for "FMEA Review".
3. PFMEA Knowledge Bank updates automatically.
        `,
        steps: [
            {
                title: 'Incident Analysis',
                description: 'Capture field data and root causes in Magnus Resolve.',
                imageUrl: '/learn/magnus_resolve_dashboard_1773450709989.png'
            },
            {
                title: 'Knowledge Mapping',
                description: 'Map resolution data back to the FMEA failure net.',
                imageUrl: '/learn/magnus_resolve_rca_tools_1773450739343.png'
            }
        ]
    }
};
